import React, { createContext, FC, useState, useRef, useEffect, useContext } from "react";
import { FeatureCollection, Geometry, GeometryCollection } from "geojson";
import { cloneDeep, countBy, Dictionary, flatten, set } from "lodash";
import type { MapRef } from "react-map-gl/mapbox";
import { featureCollection, geometryCollection } from "@turf/helpers";
import { bbox } from "@turf/bbox";
import { EasingOptions, LngLatBounds, LngLatBoundsLike } from "mapbox-gl";
import {
  MAP_BOUNDS_PADDING,
  MAP_PAN_DURATION
} from "@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants"
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { NETWORK_FILTER_CONTROL_PADDING } from "@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsMapConstants";
import { BenchmarkingCounty } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingCounty";
import { BenchmarkingState } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingState";
import { CompetitiveInsightsCountyFilterTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsCountyFilterType";
import { CompetitiveInsightsTabTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsTabTypeEnum";
import { CompetitiveInsightsMapOverlapTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsMapOverlapTypeEnum";
import { NetworkCounty } from "@app/overviews/network-benchmarking-dashboard/filters/shared/NetworkCounty";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";

interface CompetitiveInsightsMapContextValues {
  hoveredCounties: FeatureCollection<Geometry, BenchmarkingCounty>;
  isNetworkFilterControlOpen: boolean;
  mapOverlapTypes: DropdownItem<CompetitiveInsightsMapOverlapTypeEnum>[];
  mapRef: React.RefObject<MapRef>;
  onMapLoad: () => void;
  overlapCounties: Dictionary<FeatureCollection<Geometry, BenchmarkingCounty>>;
  selectedCounties: FeatureCollection<Geometry, BenchmarkingCounty>;
  selectedMapOverlapTypes: DropdownItem<CompetitiveInsightsMapOverlapTypeEnum>[];
  setIsNetworkFilterControlOpen: (isOpen: boolean) => void;
  setSelectedMapOverlapTypes: (selectedOverlapTypes: DropdownItem<CompetitiveInsightsMapOverlapTypeEnum>[]) => void;
  unselectedCounties: FeatureCollection<Geometry, BenchmarkingCounty>;
}

const initialValues: CompetitiveInsightsMapContextValues = {
  hoveredCounties: null,
  isNetworkFilterControlOpen: false,
  mapOverlapTypes: [],
  mapRef: null,
  onMapLoad: () => null,
  overlapCounties: {},
  selectedCounties: null,
  selectedMapOverlapTypes: [],
  setIsNetworkFilterControlOpen: () => null,
  setSelectedMapOverlapTypes: () => null,
  unselectedCounties: null,
}

export const CompetitiveInsightsMapContext = createContext(initialValues);

export const CompetitiveInsightsMapContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const mapRef = useRef<MapRef>(null);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  const [dataBoundingBox, setDataBoundingBox] = useState<LngLatBoundsLike | null>(null);
  const [hoveredCounties, setHoveredCounties] = useState<FeatureCollection<Geometry, BenchmarkingCounty>>(initialValues.hoveredCounties);
  const [isNetworkFilterControlOpen, setIsNetworkFilterControlOpen] = useState<boolean>(initialValues.isNetworkFilterControlOpen);
  const [mapOverlapTypes, setMapOverlapTypes] = useState<DropdownItem<CompetitiveInsightsMapOverlapTypeEnum>[]>(initialValues.mapOverlapTypes);
  const [overlapCounties, setOverlapCounties] = useState<Dictionary<FeatureCollection<Geometry, BenchmarkingCounty>>>(initialValues.overlapCounties);
  const [selectedCounties, setSelectedCounties] = useState<FeatureCollection<Geometry, BenchmarkingCounty>>(initialValues.selectedCounties);
  const [selectedMapOverlapTypes, setSelectedMapOverlapTypes] = useState<DropdownItem<CompetitiveInsightsMapOverlapTypeEnum>[]>([]);
  const [unselectedCounties, setUnselectedCounties] = useState<FeatureCollection<Geometry, BenchmarkingCounty>>(initialValues.unselectedCounties);
  const [doNotTriggerMapRebuild, setDoNotTriggerMapRebuild] = useState<boolean>(false);

  useEffect(() => {
    if (benchmarkingFiltersContext.selectedTab === CompetitiveInsightsTabTypeEnum.NetworkSelection) {
      mapRef.current?.getMap()?.resize();

      if (!!benchmarkingFiltersContext.rawCounties?.length) {
        handleZoom(benchmarkingFiltersContext.rawCounties);
      }
    }
  }, [benchmarkingFiltersContext.selectedTab]);

  const fitBoundsWithPadding = (bounds: LngLatBoundsLike): void => {
    const easingOptions: EasingOptions = {
      duration: MAP_PAN_DURATION,
      padding: {
        bottom: MAP_BOUNDS_PADDING,
        left: isNetworkFilterControlOpen ? NETWORK_FILTER_CONTROL_PADDING : MAP_BOUNDS_PADDING,
        right: MAP_BOUNDS_PADDING,
        top: MAP_BOUNDS_PADDING
      },
      retainPadding: false
    }

    mapRef.current?.fitBounds(bounds, easingOptions);
  }

  const filterNetworkCountiesByCountyFilterType = (
    network: BenchmarkingNetworkType,
    selectedCountyFilterType: CompetitiveInsightsCountyFilterTypeEnum[]
  ): NetworkCounty[] => {
    // county status filtering only applies to Your QES Networks; other networks always show all of their counties
    if (network.parentOrganization !== "Your QES Networks") {
      return network.networkCounties;
    }

    const isOpportunitySelected = selectedCountyFilterType.includes(CompetitiveInsightsCountyFilterTypeEnum.Opportunity);
    const isCurrentSelected = selectedCountyFilterType.includes(CompetitiveInsightsCountyFilterTypeEnum.Current);

    if (isOpportunitySelected && !isCurrentSelected) {
      return network.networkCounties.filter(nc => nc.countyStatus === RegionSetStatusType.Opportunity);
    }

    if (isCurrentSelected && !isOpportunitySelected) {
      return network.networkCounties.filter(nc => nc.countyStatus !== RegionSetStatusType.Opportunity);
    }

    return network.networkCounties;
  };

  const parseJSON = (json: string): FeatureCollection => {
    try {
      return JSON.parse(json) as FeatureCollection;
    } catch {
      return null;
    }
  }

  const convertToFeatureCollection = (serviceAreaMaps: BenchmarkingCounty[]): FeatureCollection<Geometry, BenchmarkingCounty> => {
    return featureCollection((serviceAreaMaps
      ? serviceAreaMaps
        .flatMap((county) =>
          parseJSON(county.geoJSON)
            ?.features.map((p) =>
              // We want everything except for the GeometryCollection as we know we are not going to receive a GeometryCollection.
              geometryCollection([p.geometry as Exclude<Geometry, GeometryCollection>],
                {
                  ...county,
                }
              )))
      : []).filter(feature => !!feature))
  };

  useEffect(() => {
    fitBoundsWithPadding(dataBoundingBox);
  }, [dataBoundingBox]);

  useEffect(() => {
    buildCountyMaps(true);
  }, [
    benchmarkingFiltersContext.selectedCounties,
    benchmarkingFiltersContext.mousedOverCountyIds,
  ]);

  useEffect(() => {
    // whenever the network flyout is toggled, the map's visibility should respond accordingly 
    if (!!benchmarkingFiltersContext.rawCounties?.length) {
      handleZoom(benchmarkingFiltersContext.rawCounties);
    }
  }, [isNetworkFilterControlOpen]);

  useEffect(() => {
    if (!!benchmarkingFiltersContext.displayedPrimaryFilters) {
      setIsNetworkFilterControlOpen(true);
    } else {
      setIsNetworkFilterControlOpen(false);
    }
  }, [benchmarkingFiltersContext.displayedPrimaryFilters]);

  useEffect(() => {
    if (!doNotTriggerMapRebuild) {
      // hovered-over counties should not display when networks are actually selected
      buildCountyMaps(false);
    } else {
      setDoNotTriggerMapRebuild(false);
    }
  }, [selectedMapOverlapTypes]);

  useEffect(() => {
    // hovered-over counties should not display when networks are actually selected
    buildCountyMaps(false, true);
  }, [benchmarkingFiltersContext.selectedNetworks]);

  useEffect(() => {
    buildCountyMaps(false, true);
  }, [benchmarkingFiltersContext.selectedCountyFilterType]);

  const buildCountyMaps = (showHover: boolean, rebuildMapOverlapTypes?: boolean) => {
    let counties = cloneDeep(benchmarkingFiltersContext.rawCounties);
    const { selectedCountyFilterType } = benchmarkingFiltersContext;

    // hovered-over counties
    if (showHover && !!benchmarkingFiltersContext.mousedOverCountyIds?.length) {
      let hoveredCountyIds = benchmarkingFiltersContext.mousedOverCountyIds;

      if (!!benchmarkingFiltersContext.mousedOverNetwork) {
        const networkCounties = filterNetworkCountiesByCountyFilterType(
          benchmarkingFiltersContext.mousedOverNetwork,
          selectedCountyFilterType
        );
        hoveredCountyIds = networkCounties.map(nc => nc.countyId.toString());
      }

      const hoveredCounties: BenchmarkingCounty[] = counties.filter(
        x => hoveredCountyIds.includes(x.countyId.toString())
      );

      counties = counties.filter(
        x => !hoveredCountyIds.includes(x.countyId.toString())
      );

      if (!!hoveredCounties) {
        const hoveredCountyBorderMap: FeatureCollection<Geometry, BenchmarkingCounty> = convertToFeatureCollection(hoveredCounties);
        setHoveredCounties(hoveredCountyBorderMap);
      }
    } else {
      const hoveredCountyBorderMap: FeatureCollection<Geometry, BenchmarkingCounty> = convertToFeatureCollection([]);
      setHoveredCounties(hoveredCountyBorderMap);
    }

    // overlap counties
    // first, obtain all of the selected county ids from each selected network,
    // then count how many times each county id appears
    const countyCounts = countBy(flatten(benchmarkingFiltersContext.selectedNetworks.map(x => {
      const networkCounties = filterNetworkCountiesByCountyFilterType(x, selectedCountyFilterType);
      return networkCounties.map(nc => nc.countyId);
    })));
    const newData: Dictionary<FeatureCollection<Geometry, BenchmarkingCounty>> = {};
    const currentMapOverlapTypes = rebuildMapOverlapTypes ? updateMapOverlapTypes(countyCounts) : selectedMapOverlapTypes;

    // next, determine which county ids have the specified overlap amount (1-5)
    Object.values(CompetitiveInsightsMapOverlapTypeEnum).map((mapOverlapType, index) => {
      const countyCountsByAmount = Object.entries(countyCounts).filter(x => x[1] === index + 1);
      const countyIdsByAmount = countyCountsByAmount.map(x => x[0]);
      let overlappedCounties: BenchmarkingCounty[] = [];

      if (currentMapOverlapTypes.map(x => x.key).includes(mapOverlapType)) {
        overlappedCounties = counties.filter(
          x => countyIdsByAmount.includes(x.countyId.toString())
        );

        counties = counties.filter(
          x => !countyIdsByAmount.includes(x.countyId.toString())
        );
      }

      const overlapCountyBorderMap: FeatureCollection<Geometry, BenchmarkingCounty> = convertToFeatureCollection(overlappedCounties);
      set(newData, mapOverlapType, overlapCountyBorderMap);
    });

    setOverlapCounties(newData);

    // unselected counties
    const unselectedRawCounties: BenchmarkingCounty[] = counties.filter(
      x => !benchmarkingFiltersContext.selectedCounties.includes(x.countyId.toString())
    );

    counties = counties.filter(
      x => benchmarkingFiltersContext.selectedCounties.includes(x.countyId.toString())
    );
    const unselectedCountyBorderMap: FeatureCollection<Geometry, BenchmarkingCounty> = convertToFeatureCollection(unselectedRawCounties);
    setUnselectedCounties(unselectedCountyBorderMap);

    // selected counties
    const selectedRawCounties: BenchmarkingCounty[] = counties.filter(
      x => benchmarkingFiltersContext.selectedCounties.includes(x.countyId.toString())
    );

    counties = counties.filter(
      x => !benchmarkingFiltersContext.selectedCounties.includes(x.countyId.toString())
    );
    const selectedCountyBorderMap: FeatureCollection<Geometry, BenchmarkingCounty> = convertToFeatureCollection(selectedRawCounties);
    setSelectedCounties(selectedCountyBorderMap);
  }

  useEffect(() => {
    // if there are counties, zoom out/in to a state
    if (!!benchmarkingFiltersContext.rawCounties?.length) {
      handleZoom(benchmarkingFiltersContext.rawCounties);
    } else {
      // if there are no counties, then a state is not selected, so zoom out/in to the entire available service area
      handleZoomToServiceArea();
    }
  }, [benchmarkingFiltersContext.rawCounties]);

  const handleZoom = (counties: BenchmarkingCounty[]) => {
    const stateCountyBorderMap: FeatureCollection<Geometry, BenchmarkingCounty> = convertToFeatureCollection(counties);

    const [
      minLongitude,
      minLatitude,
      maxLongitude,
      maxLatitude
    ] = bbox(stateCountyBorderMap);

    setDataBoundingBox(new LngLatBounds(
      [minLongitude, minLatitude], [maxLongitude, maxLatitude]
    ));
  }

  const handleZoomToServiceArea = (): void => {
    const unwantedStates = ["HI", "AK", "PR"];
    let states: BenchmarkingState[];

    if (benchmarkingFiltersContext.states.length === 1 && unwantedStates.includes(benchmarkingFiltersContext.states[0].stateAbbreviation)) {
      states = benchmarkingFiltersContext.states;
    } else {
      states = benchmarkingFiltersContext.states.filter(x => !unwantedStates.includes(x.stateAbbreviation));
    }

    handleZoom(states.flatMap(x => x.counties));
  }

  const onMapLoad = (): void => {
    if (!!benchmarkingFiltersContext.rawCounties?.length) {
      handleZoom(benchmarkingFiltersContext.rawCounties);
    } else if (!!benchmarkingFiltersContext.states?.length) {
      handleZoomToServiceArea();
    }
  }

  const updateMapOverlapTypes = (countyCounts: Dictionary<number>) => {
    const filteredMapOverlapTypes: CompetitiveInsightsMapOverlapTypeEnum[] = [];
    const previouslyUnselectedMapOverlapTypes: CompetitiveInsightsMapOverlapTypeEnum[] = [];

    for (const mapOverlapType of Object.values(CompetitiveInsightsMapOverlapTypeEnum)) {
      const index = Object.values(CompetitiveInsightsMapOverlapTypeEnum).indexOf(mapOverlapType);
      const countyCountsByAmount = Object.entries(countyCounts).filter(x => x[1] === index + 1);

      // only map overlap types that contain values should be displayed
      if (!!countyCountsByAmount.map(x => x[0]).length) {
        filteredMapOverlapTypes.push(mapOverlapType);
      }

      // if a map overlap type was previously deselected, then make sure it's still deselected
      if (mapOverlapTypes.map(x => x.key).includes(mapOverlapType) && !selectedMapOverlapTypes.map(x => x.key).includes(mapOverlapType)) {
        previouslyUnselectedMapOverlapTypes.push(mapOverlapType);
      }
    }

    const newMapOverlapTypes: DropdownItem<CompetitiveInsightsMapOverlapTypeEnum>[] = filteredMapOverlapTypes.map(x => {
      return { key: x, name: x }
    });

    const newSelectedMapOverlapTypes = cloneDeep(newMapOverlapTypes).filter(x => !previouslyUnselectedMapOverlapTypes.includes(x.key));

    setMapOverlapTypes(newMapOverlapTypes);
    setSelectedMapOverlapTypes(newSelectedMapOverlapTypes);
    setDoNotTriggerMapRebuild(true);

    return newSelectedMapOverlapTypes;
  }

  return (
    <CompetitiveInsightsMapContext.Provider value={{
      hoveredCounties,
      isNetworkFilterControlOpen,
      mapOverlapTypes,
      mapRef,
      onMapLoad,
      overlapCounties,
      selectedCounties,
      selectedMapOverlapTypes,
      setIsNetworkFilterControlOpen,
      setSelectedMapOverlapTypes,
      unselectedCounties,
    }}>
      {props.children}
    </CompetitiveInsightsMapContext.Provider>
  )
}
